import { Navbar } from "../shared/components/Navbar"
import { useLanguage } from "../shared/context/LanguageContext"
import { Fragment, useCallback, useEffect, useState } from "react"
import { ChevronDown, ChevronRight } from "lucide-react"
import { DashedLine } from "../shared/components/DashedLine"
import type { ApiFaqCategory, FaqAnswer, FaqCategory } from "../shared/helpers/faqTypes"

export function FAQ() {
  const { translation, isRTL, lang } = useLanguage()
  const [open, setOpen] = useState(false)
  const [categories, setCategories] = useState<FaqCategory[]>([])
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const selectedCategory = categories.find((category) => category.id === selectedCategoryId)
  const displayedQuestions = selectedCategory?.questions ?? []

  const fetchFaqs = useCallback(
    async (signal?: AbortSignal) => {
      setIsLoading(true)
      setErrorMessage(null)
      try {
        const url = new URL("https://dev.api.nowpay.cloud/api/v3.2/faqs")
        if (lang === "ar") {
          url.searchParams.set("lang", "ar")
        }
        const response = await fetch(url.toString(), { signal })
        if (!response.ok) {
          throw new Error(translation.faqFetchFailed)
        }
        const payload = (await response.json()) as { data?: ApiFaqCategory[] }
        const nextCategories = (
          payload?.data?.map((category, categoryIndex) => {
            const name =
              lang === "ar"
                ? category.categoryNameAr ?? category.categoryName ?? category.categoryNameEn ?? ""
                : category.categoryNameEn ?? category.categoryName ?? category.categoryNameAr ?? ""
            const questions = (category.questions ?? []).map((question, questionIndex) => {
              const resolvedQuestion =
                lang === "ar"
                  ? question.questionAr ?? question.question ?? question.questionEn ?? ""
                  : question.questionEn ?? question.question ?? question.questionAr ?? ""
              const answers: FaqAnswer[] =
                Array.isArray(question.answers) && question.answers.length > 0
                  ? question.answers.map((answer) => ({ answer }))
                  : [
                      {
                        answer:
                          lang === "ar"
                            ? question.answerAr ?? question.answer ?? question.answerEn ?? ""
                            : question.answerEn ?? question.answer ?? question.answerAr ?? "",
                      },
                    ]
              return {
                id: question.questionId ?? questionIndex,
                question: resolvedQuestion,
                answers,
              }
            })
            return {
              id: category.categoryId ?? categoryIndex,
              name,
              questions,
            }
          }) ?? []
        ).filter((category) => category.questions.length)

        setCategories(nextCategories)
        setSelectedCategoryId((prev) => {
          if (prev && nextCategories.some((category) => category.id === prev)) {
            return prev
          }
          return nextCategories[0]?.id ?? null
        })
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return
        }
        setErrorMessage(translation.faqLoadError)
      } finally {
        setIsLoading(false)
      }
    },
    [lang, translation]
  )

  useEffect(() => {
    const controller = new AbortController()
    fetchFaqs(controller.signal)
    return () => controller.abort()
  }, [fetchFaqs])

  const selectCategory = (categoryId: number) => {
    setSelectedCategoryId(categoryId)
    setOpen(false)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "smooth" })
      })
    })
  }

  return (
    <main>
      <div className="sticky top-5 sm:top-0 md:relative z-50 mx-5 md:mx-7 lg:mx-10 xl:mx-[3.125rem] bg-secondary rounded-3xl">
        <Navbar />
      </div>
      <div className="flex flex-col gap-5 px-5 md:gap-9 md:py-9 md:px-9 lg:flex-row lg:gap-20 lg:py-0 lg:pt-[4.375rem] lg:px-[5.625rem]">
        <div className="flex flex-col gap-4 lg:gap-[1.875rem] m-w-fit lg:w-[18rem] xl:w-[21rem] px-5 pt-9 lg:px-0 lg:pt-0 lg:sticky lg:top-8 lg:self-start">
          <h2 className="text-[1.75rem] md:text-3xl lg:text-4xl xl:text-[2.5rem] !leading-tight font-bold text-primary">{translation.faqTitle}</h2>
          <section>
            {/* Mobile */}
            <div className="block lg:hidden">
              <DashedLine />
              <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between my-2.5 text-left text-base md:text-xl"
              >
                <span className={"text-primary font-bold"}>
                  {selectedCategory?.name ?? (isLoading ? translation.faqLoading : translation.faqEmpty)}
                </span>
                <ChevronDown
                  className={`w-5 h-5 text-primary transition-transform ${open ? "rotate-180" : ""}`}
                />
              </button>
              <DashedLine />
              {open && categories.length > 0 && (
                <ul className="space-y-2.5 mt-2.5">
                  {categories.map((item, index) => (
                    <Fragment key={item.id}>
                      <li
                        className={`text-base md:text-xl text-primary hover:text-hoverColor cursor-pointer`}
                        onClick={() => selectCategory(item.id)}
                      >
                        {item.name}
                      </li>
                      {index === categories.length - 1 && <DashedLine />}
                    </Fragment>
                  ))}
                </ul>
              )}
            </div>
            {/* Desktop */}
            <ul className="hidden lg:block space-y-[0.625rem]">
              {categories.map((item) => (
                <Fragment key={item.id}>
                  <DashedLine isSidebar={true} />
                  <li
                    className="flex items-center justify-between cursor-pointer hover:text-hoverColor"
                    onClick={() => selectCategory(item.id)}
                  >
                    <span className={`text-primary lg:text-xl xl:text-[1.375rem] ${selectedCategoryId === item.id ? "font-bold" : ""}`}>{item.name}</span>
                    {selectedCategoryId === item.id && <ChevronRight size={24} color="#004E64" className={`hover:text-hoverColor transition-colors ${isRTL ? "rotate-180" : ""}`} />}
                  </li>
                </Fragment>
              ))}
            </ul>
          </section>
        </div>
        <div className="w-full lg:w-2/3 flex md:justify-center items-start">
          <div className="w-full flex flex-col gap-6 lg:gap-[1.875rem] md:min-h-[calc(100vh-10rem)] px-5 lg:px-0 pb-[1.875rem] lg:pb-0">
            {isLoading ? (
              <div className="text-base md:text-lg lg:text-[1.375rem] text-primary">{translation.faqLoading}</div>
            ) : errorMessage ? (
              <div className="flex flex-col gap-4 text-base md:text-lg lg:text-[1.375rem] text-primary">
                <span>{errorMessage}</span>
                <button
                  type="button"
                  onClick={() => fetchFaqs()}
                  className="w-fit px-4 py-2 rounded-full bg-primary text-white text-sm md:text-base"
                >
                  {translation.faqRetry}
                </button>
              </div>
            ) : displayedQuestions.length === 0 ? (
              <div className="text-base md:text-lg lg:text-[1.375rem] text-primary">{translation.faqEmpty}</div>
            ) : (
              displayedQuestions.map((item, index) => (
                <div key={item.id ?? index} className="flex flex-col gap-2.5">
                  <div className="text-base font-bold md:text-lg lg:text-[1.375rem] text-primary whitespace-pre-line !leading-[130%] lg:leading-9">
                    {item.question}
                  </div>
                  <ul className="flex flex-col gap-2">
                    {item.answers.map((answer, i) => (
                      <li
                        key={i}
                        className="text-base font-normal md:text-lg lg:text-[1.375rem] text-black ms-6 md:ms-7 lg:ms-8 list-disc whitespace-pre-line !leading-[130%] lg:leading-9"
                      >
                        {answer.answer}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-2.5 md:mt-3 lg:mt-5">
                    {index !== displayedQuestions.length - 1 && <DashedLine />}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </main>
  )
}
